import React, { useState, useMemo } from 'react'
import {
  SERVICE_CATEGORIES,
  getServiceCategory,
  getServiceBadgeClass,
  groupServicesByCategory,
} from '@/shared/constants/services'
import { useProductServices, ProductService } from '@/shared/hooks/useProductServices'
import { Badge } from '@goodday-clone/ui'
import { Check, Plus, X, Layers, Sparkles } from 'lucide-react'

interface ServicesPickerProps {
  selectedServices: string[]
  onChange: (services: string[]) => void
  disabled?: boolean
  label?: string
  description?: string
  readOnly?: boolean
  /** When true, each service option shows its master-data rate */
  showRates?: boolean
  /** Master-data products/services to look up rates from (name → defaultRate) */
  masterData?: ProductService[]
}

export function ServicesPicker({
  selectedServices = [],
  onChange,
  disabled = false,
  label = 'Services',
  description = 'Select services relevant to this lead or deal across Technology, Marketing & Growth, and Creative.',
  readOnly = false,
  showRates = false,
  masterData: masterDataProp,
}: ServicesPickerProps) {
  const { data: masterDataFromHook = [] } = useProductServices({ isActive: true })

  const [activeTab, setActiveTab] = useState<'all' | 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE'>('all')

  // Resolve the actual master-data set from the prop or the hook
  const masterData = masterDataProp || masterDataFromHook

  // Build a name → ProductService lookup (case-insensitive)
  const masterDataByName = useMemo(() => {
    const map: Record<string, ProductService> = {}
    for (const ps of masterData) {
      if (ps.name) map[ps.name.toLowerCase()] = ps
    }
    return map
  }, [masterData])

  const toggleService = (service: string) => {
    if (disabled || readOnly) return
    if (selectedServices.includes(service)) {
      onChange(selectedServices.filter((s) => s !== service))
    } else {
      onChange([...selectedServices, service])
    }
  }

  const removeService = (service: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled || readOnly) return
    onChange(selectedServices.filter((s) => s !== service))
  }

  const dynamicCategories = useMemo(() => {
    // Start with empty service arrays for the known categories
    const categories = SERVICE_CATEGORIES.map(c => ({ ...c, services: [] as string[] }))
    
    // The options come only from active Master Data services. There is no
    // hardcoded fallback: with none configured, the list is empty.
    for (const ps of masterData) {
      if (!ps.name) continue
      const catKey = ps.category === 'MARKETING_GROWTH' ? 'MARKETING & GROWTH' : ps.category
      const cat = categories.find(c => c.category === catKey)
      if (cat) {
        cat.services.push(ps.name)
      }
    }
    
    // Ensure currently selected services are always visible
    for (const svc of selectedServices) {
      const isKnown = categories.some(c => c.services.includes(svc))
      if (!isKnown) {
        const origCat = SERVICE_CATEGORIES.find(c => c.services.includes(svc))
        if (origCat) {
          const cat = categories.find(c => c.category === origCat.category)
          if (cat) cat.services.push(svc)
        } else {
          categories[0].services.push(svc) // Put unknown in the first category
        }
      }
    }
    
    return categories
  }, [masterData, selectedServices])

  const filteredCategories = (
    activeTab === 'all'
      ? dynamicCategories
      : dynamicCategories.filter((c) => c.category === activeTab)
  ).filter((c) => c.services.length > 0)
  const noServicesAvailable = dynamicCategories.every((c) => c.services.length === 0)

  // Compute the estimated total from master data for the currently selected services
  const estimatedTotal = useMemo(() => {
    let total = 0
    for (const svc of selectedServices) {
      const md = masterDataByName[svc.toLowerCase()]
      // A service that is not (or no longer) in Master Data has no rate; no invented price.
      if (md) total += md.defaultRate || 0
    }
    return total
  }, [selectedServices, masterDataByName])

  const formatCurrency = (amount: number) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(amount % 100000 === 0 ? 0 : 1)}L`
    }
    return `₹${amount.toLocaleString('en-IN')}`
  }

  return (
    <div className="space-y-3">
      {/* Header with Selected Counter + Estimated Total */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-primary" />
          <label className="text-xs font-bold text-foreground uppercase tracking-wider">
            {label}
          </label>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            {selectedServices.length} selected
          </span>
        </div>

        {!readOnly && selectedServices.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
          >
            Clear all
          </button>
        )}
      </div>

      {description && (
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {description}
        </p>
      )}

      {/* Estimated Amount Banner */}
      {selectedServices.length > 0 && (
        <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
          <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
            Estimated Total (from Master Data)
          </span>
          <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200">
            {formatCurrency(estimatedTotal)}
          </span>
        </div>
      )}

      {/* Selected Services Segregated by Category with Rate */}
      {selectedServices.length > 0 && (
        <div className="space-y-2.5 p-2.5 rounded-lg bg-muted/30 border border-border/60">
          {groupServicesByCategory(selectedServices, masterData).map(({ category, services: svcs }) => (
            <div key={category.category} className="space-y-1">
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: category.color }} />
                <span>{category.category}</span>
                <span className="text-[10px] text-muted-foreground/60 font-medium">({svcs.length})</span>
              </div>
              <div className="flex flex-wrap gap-1.5 pl-2.5">
                {svcs.map((svc) => {
                  const md = masterDataByName[svc.toLowerCase()]
                  const rate = md?.defaultRate
                    ? formatCurrency(md.defaultRate)
                    : null
                  return (
                    <span
                      key={svc}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border shadow-2xs transition-all ${category.badgeClass}`}
                    >
                      <Sparkles className="h-3 w-3 opacity-70" />
                      <span>{svc}</span>
                      {showRates && rate && (
                        <span className="text-[10px] opacity-70 font-mono">
                          ({rate})
                        </span>
                      )}
                      {!readOnly && !disabled && (
                        <button
                          type="button"
                          onClick={(e) => removeService(svc, e)}
                          className="ml-0.5 hover:opacity-100 opacity-60 hover:text-destructive transition-opacity cursor-pointer"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* If readOnly and no services selected */}
      {readOnly && selectedServices.length === 0 && (
        <div className="p-3 text-xs text-muted-foreground italic rounded-lg bg-muted/20 border border-border/40 text-center">
          No services assigned yet
        </div>
      )}

      {/* Interactive Selection Grid */}
      {!readOnly && (
        <div className="space-y-3">
          {/* Category Tabs */}
          <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/50 border border-border/60 text-xs overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer shrink-0 ${
                activeTab === 'all'
                  ? 'bg-background text-foreground shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All Categories
            </button>
            {SERVICE_CATEGORIES.map((cat) => (
              <button
                key={cat.category}
                type="button"
                onClick={() => setActiveTab(cat.category as any)}
                className={`px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer shrink-0 ${
                  activeTab === cat.category
                    ? 'bg-background text-foreground shadow-2xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {cat.category}
              </button>
            ))}
          </div>

          {/* Categorized Grid */}
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {filteredCategories.length === 0 && (
              <div className="p-3 text-xs text-muted-foreground italic rounded-lg bg-muted/20 border border-border/40 text-center">
                {noServicesAvailable
                  ? 'No active services in Master Data. Add or enable them under Settings → Master Data → Products & Services.'
                  : 'No active services in this category.'}
              </div>
            )}
            {filteredCategories.map((cat) => (
              <div key={cat.category} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className="text-[11px] font-bold text-muted-foreground tracking-wider uppercase">
                    {cat.category}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {cat.services.map((svc) => {
                    const isSelected = selectedServices.includes(svc)
                    const md = masterDataByName[svc.toLowerCase()]
                    return (
                      <button
                        key={svc}
                        type="button"
                        onClick={() => toggleService(svc)}
                        disabled={disabled}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium text-left border transition-all cursor-pointer ${
                          isSelected
                            ? `${cat.badgeClass} ring-1 ring-primary/40 font-semibold shadow-2xs`
                            : 'bg-background hover:bg-muted/50 border-input text-foreground hover:border-border'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="truncate mr-2">{svc}</span>
                        {showRates && md && (
                          <span className="text-[10px] text-muted-foreground font-mono shrink-0 hidden sm:inline">
                            {formatCurrency(md.defaultRate)}
                          </span>
                        )}
                        {isSelected ? (
                          <div className="h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                            <Check className="h-2.5 w-2.5 stroke-[3]" />
                          </div>
                        ) : (
                          <Plus className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
export default ServicesPicker
